import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Box } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";
import MainLayout from "./components/MainLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider, useToast } from "./components/Toast";
import Login from "./screens/public/Login";
import Dashboard from "./screens/protected/Dashboard";
import Analytics from "./screens/protected/Analytics";
import Messages from "./screens/protected/Messages";
import Payments from "./screens/protected/Payments";
import AdminStores from "./screens/protected/AdminStores";
import NotFound from "./screens/public/NotFound";
import LandingAnimated from "./screens/public/LandingAnimated";
import Offers from "./screens/public/Offers";
import OfferDetails from "./screens/public/OfferDetails";
import Stores from "./screens/public/Stores";
import StoreDetails from "./screens/public/StoreDetails";
import Events from "./screens/public/Events";
import EventDetails from "./screens/public/EventDetails";
import MallHub from "./screens/public/MallHub";
import Explore from "./screens/public/Explore";
import { ROUTES } from "./constants/routes";

/**
 * AppContent Component
 *
 * Inner component that has access to toast context for handling logout notifications.
 */
const AppContent: React.FC = () => {
  const toast = useToast();

  const handleLoginSuccess = () => {
    // Login success is handled by Redux state in ProtectedRoute
  };

  // Remove splash screen when React app is ready
  useEffect(() => {
    const splashScreen = document.getElementById("splash-screen");
    if (splashScreen) {
      // Add fade-out class
      splashScreen.classList.add("fade-out");

      // Remove from DOM after transition
      setTimeout(() => {
        splashScreen.remove();
      }, 400);
    }
  }, []);

  // Listen for logout reason events
  useEffect(() => {
    const handleLogoutReason = (event: CustomEvent) => {
      toast.warning(event.detail.message, "Session Expired");
    };

    window.addEventListener(
      "showLogoutReason" as any,
      handleLogoutReason as EventListener,
    );

    return () => {
      window.removeEventListener(
        "showLogoutReason" as any,
        handleLogoutReason as EventListener,
      );
    };
  }, [toast]);

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* ==================== PUBLIC ROUTES ==================== */}
          {/* Landing Page */}
          <Route
            path={ROUTES.HOME}
            element={
              <PublicRoute>
                <LandingAnimated />
              </PublicRoute>
            }
          />

          {/* Explore Page - Smart gateway to discover malls, stores, offers, events */}
          <Route
            path={ROUTES.EXPLORE}
            element={
              <PublicRoute>
                <Explore />
              </PublicRoute>
            }
          />

          {/* Offers Page - Public, no authentication required */}
          <Route
            path={ROUTES.OFFERS}
            element={
              <PublicRoute>
                <Offers />
              </PublicRoute>
            }
          />

          {/* Offer Details Page - Public, no authentication required */}
          <Route
            path={ROUTES.OFFER_DETAILS}
            element={
              <PublicRoute>
                <OfferDetails />
              </PublicRoute>
            }
          />

          {/* Stores Page - Public, no authentication required */}
          <Route
            path={ROUTES.STORES}
            element={
              <PublicRoute>
                <Stores />
              </PublicRoute>
            }
          />

          {/* Store Details Page - Public, no authentication required */}
          <Route
            path={ROUTES.STORE_DETAILS}
            element={
              <PublicRoute>
                <StoreDetails />
              </PublicRoute>
            }
          />

          {/* Events Page - Public, no authentication required */}
          <Route
            path={ROUTES.EVENTS}
            element={
              <PublicRoute>
                <Events />
              </PublicRoute>
            }
          />

          {/* Event Details Page - Public, no authentication required */}
          <Route
            path={ROUTES.EVENT_DETAILS}
            element={
              <PublicRoute>
                <EventDetails />
              </PublicRoute>
            }
          />

          {/* Mall Hub - Unified landing page for mall offers and stores */}
          <Route
            path={ROUTES.MALL_HUB}
            element={
              <PublicRoute>
                <MallHub />
              </PublicRoute>
            }
          />

          {/* Login Page - Redirects to /app/dashboard if already authenticated */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoute
                redirectIfAuthenticated
                redirectTo={ROUTES.APP_DASHBOARD}
              >
                <Login onLoginSuccess={handleLoginSuccess} />
              </PublicRoute>
            }
          />

          {/* ==================== PROTECTED ROUTES (/app/*) ==================== */}
          <Route
            path={ROUTES.APP}
            element={
              <ProtectedRoute
                fallback={<Login onLoginSuccess={handleLoginSuccess} />}
              >
                <ErrorBoundary>
                  <Box sx={{ height: "100vh", overflow: "hidden" }}>
                    <MainLayout>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <Navigate to={ROUTES.APP_DASHBOARD} replace />
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ErrorBoundary>
                              <Dashboard />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/analytics"
                          element={
                            <ErrorBoundary>
                              <Analytics />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/messages"
                          element={
                            <ErrorBoundary>
                              <Messages />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/payments"
                          element={
                            <ErrorBoundary>
                              <Payments />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/stores"
                          element={
                            <ErrorBoundary>
                              <AdminStores />
                            </ErrorBoundary>
                          }
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </MainLayout>
                  </Box>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
};

/**
 * App Component
 *
 * Root application component with HelmetProvider for SEO and ToastProvider for global toast notifications.
 */
function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
